import { RootState } from "index";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Row, Col, Label, Card, Button, Input, } from "reactstrap";
import { Get } from "VesselMaster/vesselMaster.hooks";
import { getVesselsAction, setOnboardedVessels, setVesselID } from "Store/Generic/ReportingSlice";
import { useQuery } from "react-query";
import { queryKeyes } from "shared/queryKeys";
import { customStyle } from "shared/CommonCSS";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import * as Yup from "yup";
import ErrorTooltip from "Components/ErrorTooltip";
import { useFormik } from "formik";
import apiGlobal from "global/api.global";
import { errorToast, successToast } from "Components/Toasts";
import Message from "Components/Message";
import { useDocumentTitle } from "Components/useDocument.hooks";
import { OnboardingStatus } from "shared/constants";
import DateRangePopup from "./DateRangePopup";
import { dateTimeFormat, getDefaultVesselValue } from "GenericForms/Helper";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import { Filtervalues } from "shared/CommonInterface";
import env from "environment_system/env_system";
import { userVessels } from "utils/auth/authUtils";
interface ReportGenerationFiltersType {
    isPopupOpen?: boolean;
    setFilterValues?: (filterValues: Filtervalues) => void;
    setisPopupOpen?: (isPopupOpen: boolean) => void;
}

const ReportGenerationFilters = ({ isPopupOpen = false, setFilterValues, setisPopupOpen }: ReportGenerationFiltersType) => {
    /** State variables */
    const dispatch = useDispatch();
    const { Vessels, VesselID, onboardedVessels } = useSelector(
        (state: RootState) => state.Reporting
    );
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedRadio, setSelectedRadio] = useState("year");
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);
    const [showDownload, setShowDownload] = useState<Boolean>(false);
    const [link, setLink] = useState<string>('');
    const [dateRangePopup, setDateRangePopup] = useState(false);
    const [minDate, setMinDate] = useState<any>("");
    const [maxDate, setMaxDate] = useState<any>("");
    useDocumentTitle('Custom Report - ecoSAIL');
    let applicableVessels = userVessels();

    /** Queries */
    const { data: voyages, isLoading: voyagesLoading } = useQuery(
        [queryKeyes.vessel.VoyageList.key, VesselID],
        async () => await Get(queryKeyes.vessel.VoyageList.url(VesselID), VesselID),
        {
            staleTime: Infinity,
        }
    );
    const { data: vesselYears, isLoading: vesselYearsLoading } = useQuery(
        [queryKeyes.vessel.VesselYear.key, VesselID],
        async () => await Get(queryKeyes.vessel.VesselYear.url(VesselID), VesselID),
    );
    /**end of Queries */
    /** Handle vessel selection */
    const handleVesselSelectionChanges = (e: any) => {
        dispatch(setVesselID(e ? e?.id : null));
    };
    const validationSchema = Yup.object().shape({
        year: Yup.string().nullable(),
        startDate: Yup.string().nullable().test(
            "start-date-validation",
            "Start date should be less than end date",
            function (startDate: any) {
                const { endDate } = this.parent;
                const startDT = new Date(startDate);
                const endDT = new Date(endDate);
                const minDT = new Date(minDate);
                const maxDT = new Date(maxDate);
                if (startDT && endDT) {
                    if (startDT < minDT && startDate !== null) {
                        return new Yup.ValidationError(`Start date should be same or after ${minDate}`, undefined, `startDate`);
                    }
                    if (startDT >= maxDT) {
                        return new Yup.ValidationError(`Start date should be before ${maxDate}`, undefined, `startDate`);
                    }
                    if (endDT > maxDT) {
                        return new Yup.ValidationError(`End date should be less than ${maxDate}`, undefined, `endDate`);
                    }
                    if (startDT > endDT && endDate !== null) {
                        return new Yup.ValidationError("End date should be after start date", undefined, `endDate`);
                    }
                }
                return true;
            }
        ),
        endDate: Yup.string().nullable(),
        startReport: Yup.number()
            .nullable()
            .when('selectedRadio', {
                is: 'startDate',
                then: schema => schema.required(commonValidationMessages.required),
                otherwise: schema => schema.nullable(),
            }),
        endReport: Yup.number()
            .nullable()
            .when('selectedRadio', {
                is: 'startDate',
                then: schema => schema.required(commonValidationMessages.required),
                otherwise: schema => schema.nullable(),
            }),
        voyages: Yup.array().nullable(),
        emails: Yup.array().of(
            Yup.string().email('Invalid email format')
        ),
        selectedRadio: Yup.string().nullable().test(
            "at-least-one",
            "Please select any one report parameters",
            function () {
                const { year, startDate, voyagesNumber } = this.parent;
                // Early returns for better performance
                if (year) return true;
                if (startDate && startDate.length > 0) return true;
                if (voyagesNumber?.length > 0) return true;

                return false;
            }
        ),
    });

    /** useEffect */
    /** Populate Vessels array */
    useEffect(() => {
        dispatch(getVesselsAction("vessel_master" as string));
    }, [dispatch]);

    useEffect(() => {
        apiGlobal.get(queryKeyes.vessel.GetMinMaxReportDate.url(VesselID)).then(response => {
            setMinDate(response.data?.start_date);
            setMaxDate(response.data?.end_date);
        }).catch(err => {
            console.error(err);
        })
    }, [VesselID])

    /** Effect 2: Set onboarded vessels when Vessels data is available */
    useEffect(() => {
        if (Array.isArray(Vessels)) {
            const onboardedVessels = Vessels.filter(
                (vessel) => vessel.onboarding_status === OnboardingStatus.ONBOARDED
            );
            dispatch(setOnboardedVessels(onboardedVessels));
        }
    }, [Vessels, dispatch]);
    /** useEffect end */

    /** Formik Setup */
    const customReportUseFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            vessel_id: VesselID,
            year: null as string,
            startDate: null as string,
            endDate: null as string,
            startReport: null as number,
            endReport: null as number,
            voyages: null as number[],
            voyagesNumber: [] as string[],
            selectedRadio: 'year',
            emails: [''],
        },
        validationSchema: validationSchema,
        onSubmit: (values: any, actions: any) => {
            actions.setSubmitting(true);
            const selectedValue = selectedRadio;
            let requestBody: any = {
                vessel_id: values?.vessel_id,
                emails: values?.emails
            };

            if (selectedValue === "year") {
                requestBody.year = values.year;
            } else if (selectedValue === "startDate") {
                requestBody.start_report_id = values.startReport;
                requestBody.end_report_id = values.endReport;
            } else if (selectedValue === "voyages") {
                requestBody.voyages = values.voyages;
            }

            // Make API call
            apiGlobal.post(`/excel_download/`, requestBody)
                .then(res => {
                    if (res.status === 200) {
                        setShowDownload(true);
                        setLink(res.data?.download_url);
                        successToast("Report generated successfully!");
                    }
                })
                .catch(err => {
                    errorToast(err?.response?.data?.error || "Error generating report!");
                }).finally(() => {
                    setDateRangePopup(false);
                    actions.setSubmitting(false);
                });
        },
    });

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedValue = event.target.id;
        setSelectedRadio(selectedValue);
        customReportUseFormik.setValues({
            ...customReportUseFormik.values,
            year: selectedValue === "year" ? customReportUseFormik.values.year : null,
            startDate: selectedValue === "startDate" ? customReportUseFormik.values.startDate : null,
            endDate: selectedValue === "endDate" ? customReportUseFormik.values.endDate : null,
            voyages: selectedValue === "voyages" ? customReportUseFormik.values.voyages : null,
        });
        setRefreshKey(refreshKey + 1);
    };
    useEffect(() => {
        if (!dateRangePopup) {
            const hasErrors = customReportUseFormik?.errors && Object.keys(customReportUseFormik.errors).length > 0;
            if (hasErrors) {
                customReportUseFormik?.setValues({
                    ...customReportUseFormik.values,
                    startDate: null as string,
                    endDate: null as string,
                    startReport: null as number,
                    endReport: null as number,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRangePopup]);
    return (
        <React.Fragment>
            <Card className="p-2">
                <form
                    onSubmit={customReportUseFormik.handleSubmit}
                    noValidate
                    autoComplete="off"
                >
                    {!isPopupOpen && (
                        <Row className="mb-3">
                            <Col sm={4}>
                                <Label className="mb-1">Select vessel</Label>
                                <Select
                                    options={
                                        onboardedVessels.filter((item: any) => item.display === true &&
                                            applicableVessels?.includes(item?.id)) as any[]
                                    }
                                    id="vessel_id"
                                    getOptionLabel={
                                        (option: any) => option.is_under_maintenance ?
                                            `${option?.vessel_name_type_name} (Under Maintenance)` : option?.vessel_name_type_name
                                    }
                                    getOptionValue={(option: any) => option.id}
                                    onChange={(e: any) => {
                                        setShowDownload(false);
                                        customReportUseFormik.setFieldValue("voyages", null);
                                        customReportUseFormik.setFieldValue("year", null);
                                        customReportUseFormik.setFieldValue("startDate", null);
                                        customReportUseFormik.setFieldValue("vessel_id", e?.id);
                                        handleVesselSelectionChanges(e);
                                        setRefreshKey(refreshKey + 1);
                                    }}
                                    isClearable={true}
                                    menuPortalTarget={document.body}
                                    styles={customStyle}
                                    defaultValue={getDefaultVesselValue(
                                        VesselID,
                                        vessel,
                                        onboardedVessels,
                                        applicableVessels
                                    )}
                                    isOptionDisabled={(option: any) => option.is_under_maintenance}
                                />
                            </Col>
                            {customReportUseFormik.errors?.vessel_id && (
                                <ErrorTooltip
                                    target={`vessel_id`}
                                    message={customReportUseFormik.errors?.vessel_id}
                                    open={customReportUseFormik.errors?.vessel_id}
                                />
                            )}
                        </Row>
                    )}
                    {!getDefaultVesselValue(
                        VesselID,
                        vessel,
                        onboardedVessels,
                        applicableVessels
                    ) && (
                            <Message message="Please select a vessel to proceed" type="warning" width="365px" />
                        )}
                    <div className="mt-3">
                        <React.Fragment>
                            {!isPopupOpen &&
                                <React.Fragment>
                                    <h6> Enter Emails </h6>
                                    <Row>
                                        {customReportUseFormik.values.emails.map((email: string, index: number) => (
                                            <Row key={index} className="mb-2 align-items-end">
                                                <Col lg={3}>
                                                    <Label htmlFor={`emails_${index}`} className="mb-1">
                                                        Email {index + 1}
                                                    </Label>
                                                    <Input
                                                        type="email"
                                                        id={`emails_${index}`}
                                                        name={`emails.${index}`}
                                                        value={email}
                                                        placeholder="Enter email address"
                                                        autoComplete="off"
                                                        onChange={customReportUseFormik.handleChange}
                                                        onBlur={() =>
                                                            customReportUseFormik.setFieldTouched(`emails.${index}`, true)
                                                        } // ✅ mark individual field as touched
                                                        className={`form-control`}
                                                        disabled={!VesselID}
                                                    />

                                                    {/* ✅ Error Tooltip with touch check */}
                                                    {Array.isArray(customReportUseFormik.errors.emails) &&
                                                        Array.isArray(customReportUseFormik.touched.emails) &&
                                                        env.form_validation &&
                                                        customReportUseFormik.errors.emails[index] &&
                                                        customReportUseFormik.touched.emails[index] && (
                                                            <ErrorTooltip
                                                                target={`emails_${index}`}
                                                                message={customReportUseFormik.errors.emails[index]}
                                                                open={!!customReportUseFormik.errors.emails[index]}
                                                            />
                                                        )}
                                                </Col>
                                                <Col lg={2}>
                                                    <div className="ele_row1">
                                                        {customReportUseFormik.values.emails.length > 1 && (
                                                            <button
                                                                type="button"
                                                                className="btn"
                                                                onClick={() => {
                                                                    const newEmails = [...customReportUseFormik.values.emails];
                                                                    newEmails.splice(index, 1);
                                                                    customReportUseFormik.setFieldValue('emails', newEmails);
                                                                }}
                                                            >
                                                                <i className="dripicons-trash icon_s18" />
                                                            </button>
                                                        )}

                                                        {index === customReportUseFormik.values.emails.length - 1 && customReportUseFormik.values.emails.length <= 9 && (
                                                            <Button
                                                                type="button"
                                                                className="btn ms-1"
                                                                color="primary"
                                                                onClick={() =>
                                                                    customReportUseFormik.setFieldValue('emails', [
                                                                        ...customReportUseFormik.values.emails,
                                                                        '',
                                                                    ])
                                                                }
                                                            >
                                                                <i className="dripicons-plus icon_s18 navbar_menu" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        ))}
                                    </Row>
                                </React.Fragment>
                            }
                            <Row className="mb-4">
                                <h6 className="mb-2">Select report parameters</h6>
                                <Col sm={4}>
                                    <div className="ele_row1">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="reportParameter"
                                            id="year"
                                            disabled={!VesselID}
                                            checked={selectedRadio === "year"}
                                            onChange={(e) => {
                                                handleRadioChange(e);
                                                customReportUseFormik.setFieldValue("selectedRadio", "year");
                                                setDateRangePopup(false);
                                            }}
                                            onClick={() => {
                                                setShowDownload(false);
                                                customReportUseFormik.setFieldValue("selectedRadio", "year")
                                            }}
                                        />
                                        <div className="w-70">
                                            <Label className="mb-1 ms-2" htmlFor="year">
                                                Select year
                                            </Label>
                                            <Select
                                                key={refreshKey}
                                                options={vesselYears}
                                                getOptionLabel={(option: any) => option.year}
                                                getOptionValue={(option: any) => option.year}
                                                onChange={(option: any) => {
                                                    customReportUseFormik.setFieldValue("year", option.year);
                                                    setDateRangePopup(false);
                                                }}
                                                styles={customStyle}
                                                isDisabled={selectedRadio !== "year" || !VesselID}
                                                isLoading={vesselYearsLoading}
                                            />
                                        </div>
                                    </div>
                                </Col>
                                <Col sm={4}>
                                    <div className="ele_row1">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="reportParameter"
                                            id="startDate"
                                            disabled={!VesselID}
                                            checked={selectedRadio === "startDate"}
                                            onChange={(e) => {
                                                handleRadioChange(e);
                                                customReportUseFormik.setFieldValue("selectedRadio", "startDate");
                                            }}
                                            onClick={() => {
                                                setShowDownload(false);
                                                customReportUseFormik.setFieldValue("selectedRadio", "startDate");
                                                setDateRangePopup(true);
                                            }}
                                        />
                                        <div className="w-70">
                                            <Label className="mb-1 ms-2" htmlFor="startDate">
                                                Select date range
                                            </Label>
                                            <DateRangePopup
                                                dateRangePopup={dateRangePopup}
                                                setDateRangePopup={setDateRangePopup}
                                                customReportUseFormik={customReportUseFormik}
                                                minDate={minDate}
                                                maxDate={maxDate}
                                                setFilterValues={setFilterValues}
                                                setisPopupOpen={setisPopupOpen}
                                                isPopupOpen={isPopupOpen}
                                            /><br />
                                            <Label className="mb-0">Start date:
                                                <strong className="ms-2">
                                                    {dateTimeFormat(customReportUseFormik?.values?.startDate, false, false, true) ?? '-'}
                                                </strong>
                                            </Label><br />
                                            <Label className="mb-0" id="endDate_lbl">End date:
                                                <strong style={{ marginLeft: '0.75rem' }}>
                                                    {dateTimeFormat(customReportUseFormik?.values?.endDate, false, false, true) ?? '-'}
                                                </strong>
                                            </Label>
                                        </div>
                                    </div>
                                </Col>
                                <Col sm={4}>
                                    <div className="ele_row1">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            disabled={!VesselID}
                                            name="reportParameter"
                                            id="voyages"
                                            checked={selectedRadio === "voyages"}
                                            onChange={(e) => {
                                                setShowDownload(false);
                                                handleRadioChange(e);
                                                customReportUseFormik.setFieldValue("selectedRadio", "voyages");
                                            }}
                                            onClick={() => {
                                                customReportUseFormik.setFieldValue("selectedRadio", "voyages")
                                                setShowDownload(false);
                                            }}
                                        />
                                        <div className="w-70">
                                            <Label className="mb-1 ms-2" htmlFor="voyages">
                                                Select voyages
                                            </Label>
                                            <Select
                                                options={
                                                    voyages?.filter(
                                                        (item: any) =>
                                                            item.display === true &&
                                                            (item.voyage_status === 2 || item.voyage_status === 3)
                                                    ) || []
                                                }
                                                getOptionLabel={(option: any) => option.voyage_number}
                                                getOptionValue={(option: any) => option.id}
                                                value={
                                                    // Convert stored IDs back to option objects for React Select
                                                    voyages?.filter((voyage: any) =>
                                                        customReportUseFormik.values.voyages?.includes(voyage.id)
                                                    ) || []
                                                }
                                                onChange={(selectedOptions: any) => {
                                                    customReportUseFormik.setFieldValue(
                                                        "voyages",
                                                        selectedOptions ? selectedOptions.map((option: any) => option.id) : []
                                                    );
                                                    customReportUseFormik.setFieldValue(
                                                        "voyagesNumber",
                                                        selectedOptions ? selectedOptions.map((option: any) => option.voyage_number) : []
                                                    );
                                                }}
                                                isMulti
                                                styles={customStyle}
                                                isDisabled={selectedRadio !== "voyages" || !VesselID}
                                                key={refreshKey}
                                                isLoading={voyagesLoading}
                                            />
                                        </div>
                                    </div>
                                </Col>
                                <Col sm={12} className="mt-4">
                                    {customReportUseFormik?.errors?.selectedRadio && (
                                        <Message message={customReportUseFormik?.errors?.selectedRadio} type="danger" width="365px" />
                                    )}
                                </Col>
                            </Row>

                            <Row className="mb-2 justify-content-between">
                                <Col sm={10} className="d-flex justify-content-start align-items-center ">
                                    {showDownload === true && (
                                        <>
                                            <Button
                                                type="button"
                                                color="primary"
                                                className="btn waves-effect btn-label waves-light me-3 "
                                                disabled={!link}
                                                onClick={() => {
                                                    if (link) {
                                                        const fileName = link.split("/").pop();
                                                        window.location.href = `${env.media_base_url}/excel_exports/${fileName}`;
                                                    }
                                                }}
                                            >
                                                <i className="dripicons-download label-icon icon_s23 align-middle" />{" "}
                                                Download
                                            </Button>
                                            <span className="text-muted font-14">
                                                A copy will be emailed to the provided addresses.
                                            </span>
                                        </>
                                    )}
                                </Col>
                                {!isPopupOpen ? (
                                    <Col sm={2} className="d-flex justify-content-end ">
                                        <Button color="primary"
                                            type="submit"
                                            disabled={customReportUseFormik.isSubmitting || !VesselID}
                                            className={`${customReportUseFormik.isSubmitting && 'disabled'}`}>
                                            {customReportUseFormik.isSubmitting ? (
                                                <>
                                                    <i className="spinner-border spinner-border-sm text-light me-2" />
                                                    Generating...
                                                </>
                                            ) : (
                                                "Generate"
                                            )}
                                        </Button>
                                    </Col>
                                ) : (
                                    <Col className="d-flex justify-content-end">
                                        <Button
                                            color="primary"
                                            type="button"
                                            disabled={!customReportUseFormik?.isValid || !VesselID}
                                            onClick={() => {
                                                if (customReportUseFormik?.isValid) {
                                                    setFilterValues(customReportUseFormik.values);
                                                    setisPopupOpen(false);
                                                }
                                            }}
                                        >
                                            Apply
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </React.Fragment>
                    </div>

                    <FormValuesDebug
                        values={[
                            customReportUseFormik?.values,
                            customReportUseFormik?.errors,
                            customReportUseFormik?.touched,
                        ]}
                    />
                </form>
            </Card>
        </React.Fragment>
    );
};

export default ReportGenerationFilters;
